import type { SubmitEventHandler } from 'react';
import type { DropdownOption } from '../../components/Dropdown/DropdownOption/DropdownOption';
import type { CustomLink } from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { getTestProps } from '../../lib/helpers';

import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { useAppForm } from '../../form-hooks/form';

const EMPTY_LINK: CustomLink = {
  url_template: '',
  content_types: [],
  link_template: '',
};

interface CustomLinkModalContentProps {
  ctdOptions: DropdownOption[];
  initialLink?: CustomLink;
  disabled?: boolean;
  testId?: string;
}

const CustomLinkModalContent = ({
  ctdOptions,
  initialLink,
  disabled,
  testId,
}: CustomLinkModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const validation = useMemo(
    () =>
      z.object({
        link_template: z.string().min(1, t('SpaceSettings.Errors.MinLength')),
        url_template: z.string().min(1, t('SpaceSettings.Errors.MinLength')),
        content_types: z.array(z.string()),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: initialLink ?? EMPTY_LINK,
    validators: { onChange: validation },
    onSubmit: async ({ value }) => {
      modalInstance.resolve?.({
        url_template: value.url_template,
        link_template: value.link_template,
        content_types: value.content_types ?? [],
      });
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id="custom-links-form"
        className="w-full flex flex-col gap-4"
        onSubmit={handleSubmit}
        noValidate
        {...getTestProps(testId)}
      >
        <form.AppField name="content_types">
          {(field) => (
            <field.Dropdown
              label={t('SpaceSettings.CustomLinks.ContentTypes')}
              options={ctdOptions}
              multiple
              disabled={disabled}
              {...getTestProps(testId, 'content-types', 'testId')}
            />
          )}
        </form.AppField>

        <form.AppField name="url_template">
          {(field) => (
            <field.Input
              label={t('SpaceSettings.CustomLinks.UrlTemplate')}
              disabled={disabled}
              required
              {...getTestProps(testId, 'url-template', 'testId')}
            />
          )}
        </form.AppField>

        <form.AppField name="link_template">
          {(field) => (
            <field.Input
              label={t('SpaceSettings.CustomLinks.LinkTemplate')}
              disabled={disabled}
              required
              {...getTestProps(testId, 'link-template', 'testId')}
            />
          )}
        </form.AppField>

        <div
          className={twMerge(
            'flex justify-center gap-3 absolute bottom-3 pt-3',
            'left-1/2 -translate-x-1/2 w-full',
            'border-t border-slate-100 dark:border-slate-800',
          )}
        >
          <CancelButton
            buttonColor="blueBordered"
            type="button"
            onClick={() => modalInstance.resolve?.(false)}
            {...getTestProps(testId, 'cancel', 'testId')}
          >
            {t('Global.Cancel')}
          </CancelButton>
          <SaveButton
            buttonColor="blue"
            type="submit"
            disabled={disabled}
            {...getTestProps(testId, 'save', 'testId')}
          >
            {t('Global.Save')}
          </SaveButton>
        </div>
      </form>
    </form.AppForm>
  );
};

export default CustomLinkModalContent;
